import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Shield } from "lucide-react";
import { z } from "zod";

const totpCodeSchema = z.string().length(6, "Le code doit contenir exactement 6 chiffres").regex(/^\d+$/, "Le code ne doit contenir que des chiffres");

interface MFAVerificationProps {
  factorId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const MFAVerification = ({ factorId, onSuccess, onCancel }: MFAVerificationProps) => {
  const { verifyMFA } = useAuth();
  const { toast } = useToast();
  
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      totpCodeSchema.parse(code);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: "Code invalide",
          description: err.errors[0]?.message ?? "",
          variant: "destructive",
        });
        return;
      }
    }

    setVerifying(true);
    const { error } = await verifyMFA(factorId, code);
    setVerifying(false);

    if (error) {
      toast({
        title: "Échec de la vérification",
        description: "Code invalide. Veuillez réessayer.",
        variant: "destructive",
      });
      setCode("");
      return;
    }

    onSuccess();
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <Shield className="w-12 h-12 text-primary" />
        </div>
        <CardTitle className="font-serif text-2xl">Authentification à deux facteurs</CardTitle>
        <CardDescription>
          Entrez le code à 6 chiffres de votre application d'authentification
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-code">Code de vérification</Label>
            <Input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-widest"
              autoFocus
              required
            />
          </div>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={verifying || code.length !== 6}
          >
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Vérification...
              </>
            ) : (
              "Vérifier"
            )}
          </Button>
          <Button 
            type="button" 
            variant="ghost" 
            className="w-full" 
            onClick={onCancel}
          >
            Annuler
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
